import { Component, useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider, useAuth } from "@/lib/auth";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import AdminShell from "@/components/admin/AdminShell";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceSeriesPage from "@/pages/ServiceSeries";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Insights from "@/pages/Insights";
import PostDetail from "@/pages/PostDetail";
import Media from "@/pages/Media";
import Frameworks from "@/pages/Frameworks";
import TestimonialsPage from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import BookCall from "@/pages/BookCall";
import CatchRedirect from "@/pages/CatchRedirect";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import CollectionAdmin from "@/pages/admin/CollectionAdmin";
import LeadsAdmin from "@/pages/admin/LeadsAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LenisProvider() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function Analytics() {
  useEffect(() => {
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (!gaId) return;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", gaId);
  }, []);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error(err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "120px 24px", maxWidth: 640, margin: "0 auto", fontFamily: "Manrope, sans-serif" }}>
          <h1 className="font-serif text-4xl">Something went wrong.</h1>
          <p className="mt-4 text-slategray">Please refresh the page. The issue has been logged.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function SiteLayout() {
  return (
    <div className="min-h-screen bg-paper text-obsidian font-sans">
      <SiteHeader />
      <main>
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function ProtectedAdmin() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      </div>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <LenisProvider />
          <ScrollToTop />
          <Analytics />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceSeriesPage />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<PostDetail />} />
              <Route path="/media" element={<Media />} />
              <Route path="/frameworks" element={<Frameworks />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<BookCall />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdmin />}>
              <Route index element={<AdminDashboard />} />
              <Route path="case-studies" element={<CollectionAdmin key="case-studies" name="case-studies" />} />
              <Route path="services" element={<CollectionAdmin key="services" name="services" />} />
              <Route path="posts" element={<CollectionAdmin key="posts" name="posts" />} />
              <Route path="testimonials" element={<CollectionAdmin key="testimonials" name="testimonials" />} />
              <Route path="team" element={<CollectionAdmin key="team" name="team" />} />
              <Route path="media" element={<CollectionAdmin key="media" name="media" />} />
              <Route path="clients" element={<CollectionAdmin key="clients" name="clients" />} />
              <Route path="redirects" element={<CollectionAdmin key="redirects" name="redirects" />} />
              <Route path="leads" element={<LeadsAdmin />} />
              <Route path="settings" element={<SettingsAdmin />} />
            </Route>
            <Route path="*" element={<CatchRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
